// nav-hierarchy/resolve.ts
// Pure path-resolution helper. Maps a URL path to AntD Menu's selectedKeys +
// openKeys via exact match then longest-prefix fallback. Structural only —
// no policy.

import type { NavItem, NavSection } from './types';

export interface ResolvedSelection {
  selectedKeys: string[];
  openKeys: string[];
}

const EMPTY: ResolvedSelection = { selectedKeys: [], openKeys: [] };

/**
 * Given the sectioned IA, flat bottom items, and the current URL path,
 * return the selectedKeys and openKeys that AntD Menu needs to highlight
 * the active item and expand its ancestors.
 *
 * Rules:
 *   1. Exact path match wins. When multiple items share the same path
 *      (common pattern: parent `Scans` and child `Scans > Overview` both
 *      use `/scans`), prefer the DEEPEST item so the submenu stays open
 *      and the leaf is highlighted rather than the section header.
 *   2. Longest-prefix match falls back. On path-length ties, prefer the
 *      deepest item for the same reason.
 *   3. No match → empty selection.
 */
export function resolveSelectedFromPath(
  ia: readonly NavSection[],
  bottomItems: readonly NavItem[],
  currentPath: string | undefined,
): ResolvedSelection {
  if (!currentPath) return EMPTY;

  const entries: { item: NavItem; ancestors: string[] }[] = [];
  const walk = (items: readonly NavItem[], ancestors: string[]): void => {
    for (const item of items) {
      entries.push({ item, ancestors });
      if (item.children?.length) walk(item.children, [...ancestors, item.key]);
    }
  };
  for (const section of ia) walk(section.items, []);
  walk(bottomItems, []);

  const exactMatches = entries.filter((e) => e.item.path === currentPath);
  if (exactMatches.length > 0) {
    const exact = exactMatches.reduce(
      (deepest, e) => (e.ancestors.length > deepest.ancestors.length ? e : deepest),
      exactMatches[0],
    );
    return { selectedKeys: [exact.item.key], openKeys: exact.ancestors };
  }

  let best: { item: NavItem; ancestors: string[] } | null = null;
  for (const e of entries) {
    if (!e.item.path) continue;
    if (!currentPath.startsWith(e.item.path)) continue;
    const after = currentPath[e.item.path.length];
    if (after !== undefined && after !== '/') continue;
    if (!best) {
      best = e;
      continue;
    }
    const bestPathLen = best.item.path?.length ?? 0;
    if (e.item.path.length > bestPathLen) {
      best = e;
    } else if (e.item.path.length === bestPathLen && e.ancestors.length > best.ancestors.length) {
      best = e;
    }
  }
  if (best) return { selectedKeys: [best.item.key], openKeys: best.ancestors };

  return EMPTY;
}
